import { useCallback, useEffect, useMemo, useState } from 'react'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import CompleteLogoNexAtlas from '@assets/complete-logo-nexatlas-light-text.svg?react'
import { CircularProgress } from '@mui/material'
import { useTheme } from 'styled-components'

import { InjectionTokens } from '@/controller/tokens'

import { DrawerItemProps } from '../../containers/DrawerPresenter/DrawerPresenter'
import { ModalPresenter } from '../../containers/ModalPresenter'
import { ToastPresenter } from '../../containers/ToastPresenter'
import { LoadingState } from '../../states/LoadingState'
import { AppErrorBoundary } from '../AppErrorBoundary/AppErrorBoundary'
import { BetaPill } from '../BetaPill'
import {
  Container,
  LogoContainer,
  MainDrawer,
  MainDrawerContent,
  MainDrawerItem,
  NotificationDot,
  OptionButton,
  OptionButtonWrapper,
  OptionContainer,
  SideBar,
  SideBarContent,
  SideBarGroup
} from './Drawer.styles'
import { DrawerContentWidthVariant } from './Drawer.types'
import { fullScreenPaths } from './utils'

import type { ILogger } from '@/infra/logger/ILogger'
import { useChangeTabWatcher } from '@/presentation/contexts/ChangeTabContext'
import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useScreenDimensions } from '@/presentation/hooks/useScreenDimensions'
import { FeedbackFeatureImperativeInterface } from '@/presentation/interfaces/FeedbackFeatureImperativeInterface'
import { DrawerRouter } from '@/presentation/router/DrawerRouter'
import { RouterPaths } from '@/presentation/router/RouterPathsMapper'
import { buildUnsavedChangesExitModal } from '@/presentation/utils/unsavedChangesExitModal'
import { ModalUtil } from '@/utils/modalUtil'

export interface DrawerProps {
  defaultOption: RouterPaths
  topItems: DrawerItemProps[]
  bottomItems: DrawerItemProps[]
  isMainDrawerOpen: boolean
  setIsMainDrawerOpen: (isMainDrawerOpen: boolean) => void
  activeOption: RouterPaths
  setActiveOption: (activeOption: RouterPaths) => void
  additionalBetaRoutes?: (string | RegExp)[]
  contentWidthVariant?: DrawerContentWidthVariant
}

function Drawer(props: DrawerProps) {
  const {
    defaultOption,
    topItems,
    bottomItems,
    activeOption,
    isMainDrawerOpen,
    setIsMainDrawerOpen,
    additionalBetaRoutes = [],
    contentWidthVariant = 'default'
  } = props

  const theme = useTheme()
  const location = useLocation()
  const navigate = useNavigate()

  // Top-level drawer section (e.g. 'my-content', 'settings'). Used to reset the
  // error boundary only when switching between major sections — keying it by the
  // full pathname instead would remount the whole drawer subtree on every
  // sub-navigation (tab switches, create/edit), discarding scroll position and
  // in-screen state.
  const drawerSection = location.pathname.split('/')[1] ?? ''
  const logger = useInjection<ILogger>(InjectionTokens.Logger)

  const changeTabWatcher = useChangeTabWatcher()

  const [isFullscreen, setIsFullscreen] = useState(false)

  const loadingState = useBehaviorSubject(LoadingState)

  const isBeta = useMemo(() => {
    const isItemBeta = [...topItems, ...bottomItems].find((item) => activeOption.startsWith(item.routePath))?.isInBeta
    const isRouteBeta = additionalBetaRoutes.some((route) => {
      if (typeof route === 'string') {
        return activeOption.startsWith(route)
      }
      return route.test(activeOption)
    })

    return isItemBeta || isRouteBeta
  }, [activeOption, topItems, bottomItems, additionalBetaRoutes])

  const isOptionContainerOpen = useMemo(() => {
    return activeOption !== defaultOption
  }, [activeOption, defaultOption])

  useEffect(() => {
    if (activeOption) setIsMainDrawerOpen(false)
  }, [activeOption, setIsMainDrawerOpen])

  useEffect(() => {
    const match = fullScreenPaths.some((path) => matchPath({ path, end: true }, location.pathname))
    setIsFullscreen((prev) => {
      if (prev !== match) {
        FeedbackFeatureImperativeInterface.setAppInFullscreen(match)
        return match
      }
      return prev
    })
  }, [location.pathname])

  const handleOptionCLick = useCallback(
    (itemRoutePath: RouterPaths) => {
      const fallbackRoute = changeTabWatcher.discardRoute || changeTabWatcher.tabToWatch || defaultOption

      const handleDiscardChanges = () => {
        if (changeTabWatcher.onDiscardAndExit) {
          changeTabWatcher.onDiscardAndExit({ targetRoute: fallbackRoute })
          return
        }
        navigate(fallbackRoute)
      }

      const showUnsavedChangesModal = () => {
        if (changeTabWatcher.onSaveAndExit) {
          ModalUtil.drawer.show(
            buildUnsavedChangesExitModal({
              onSave: () => changeTabWatcher.onSaveAndExit({ targetRoute: itemRoutePath }),
              onDiscard: () => {
                if (changeTabWatcher.onDiscardAndExit) {
                  changeTabWatcher.onDiscardAndExit({ targetRoute: itemRoutePath })
                  return
                }
                navigate(itemRoutePath)
              }
            })
          )
          return
        }

        ModalUtil.drawer.show({
          title: 'Alterações não salvas',
          headerDescription: 'Suas mudanças serão perdidas se você sair dessa aba sem salvar.',
          positiveLabel: 'Descartar alterações',
          negativeLabel: 'Continuar editando',
          positiveColor: 'danger',
          onPositive: handleDiscardChanges,
          onNegative: () => {}
        })
      }

      if (activeOption) {
        if (activeOption === itemRoutePath && !isMainDrawerOpen) {
          navigate(defaultOption)
        } else if (activeOption === itemRoutePath && isMainDrawerOpen) {
          setIsMainDrawerOpen(false)
        } else {
          if (changeTabWatcher.isWatching && activeOption !== itemRoutePath) {
            showUnsavedChangesModal()
          } else {
            navigate(itemRoutePath)
          }
        }
      } else {
        if (changeTabWatcher.isWatching) {
          showUnsavedChangesModal()
        } else {
          navigate(itemRoutePath)
        }
      }
    },
    [
      activeOption,
      changeTabWatcher.discardRoute,
      changeTabWatcher.isWatching,
      changeTabWatcher.onDiscardAndExit,
      changeTabWatcher.onSaveAndExit,
      changeTabWatcher.tabToWatch,
      defaultOption,
      isMainDrawerOpen,
      navigate,
      setIsMainDrawerOpen
    ]
  )

  return (
    <Container
      data-testid="Drawer/Container"
      isOpen={isOptionContainerOpen || isMainDrawerOpen}
      isFullscreen={isFullscreen}
    >
      <SideBar hasBorder={isMainDrawerOpen || isOptionContainerOpen} isFullscreen={isFullscreen}>
        <OptionButton
          data-testid="Drawer/ToggleButton"
          isActive={isMainDrawerOpen}
          $should_show_tooltip={false}
          size="sm"
          color="ghost"
          iconColor={isMainDrawerOpen ? theme.colors.primaryL2 : theme.colors.neutralL5}
          icon="HamburgerMenu"
          onClick={() => setIsMainDrawerOpen(!isMainDrawerOpen)}
        />
        <SideBarContent>
          <SideBarGroup>
            {topItems.map((item) => (
              <OptionButtonWrapper key={item.routePath} style={{ position: 'relative' }}>
                <OptionButton
                  data-testid="Drawer/ToggleButton"
                  label={item.title}
                  isActive={activeOption.startsWith(item.routePath)}
                  $should_show_tooltip={!isMainDrawerOpen}
                  size="sm"
                  color="ghost"
                  iconColor={activeOption.startsWith(item.routePath) ? theme.colors.primaryL2 : theme.colors.neutralL5}
                  icon={item.icon}
                  onClick={() => handleOptionCLick(item.routePath)}
                />
                {item.isInBeta && (
                  <BetaPill
                    label="beta"
                    isActive={activeOption.startsWith(item.routePath)}
                    position={{
                      bottom: 'var(--drawer-beta-pill-bottom)'
                    }}
                  />
                )}
                {item.hasNotification && <NotificationDot />}
              </OptionButtonWrapper>
            ))}
          </SideBarGroup>
          <SideBarGroup>
            {loadingState.isLoading && (
              <CircularProgress
                size={20}
                style={{
                  alignSelf: 'center'
                }}
              />
            )}
            {bottomItems.map((item) => (
              <OptionButton
                key={item.routePath}
                data-testid="Drawer/ToggleButton"
                label={item.title}
                $should_show_tooltip={!isMainDrawerOpen}
                isActive={activeOption.startsWith(item.routePath)}
                size="sm"
                color="ghost"
                iconColor={activeOption.startsWith(item.routePath) ? theme.colors.primaryL2 : theme.colors.neutralL5}
                icon={item.icon}
                onClick={() => handleOptionCLick(item.routePath)}
              >
                {item.isInBeta && (
                  <BetaPill
                    label="beta"
                    isActive={activeOption.startsWith(item.routePath)}
                    position={{
                      bottom: 'var(--drawer-beta-pill-bottom)'
                    }}
                  />
                )}
                {item.hasNotification && <NotificationDot />}
              </OptionButton>
            ))}
          </SideBarGroup>
        </SideBarContent>
      </SideBar>
      <OptionContainer
        isOpen={isOptionContainerOpen}
        isFullscreen={isFullscreen}
        isBeta={isBeta}
        $contentWidthVariant={contentWidthVariant}
      >
        <AppErrorBoundary
          logger={logger}
          boundaryName="DrawerContentBoundary"
          title="Falha ao carregar esta aba"
          message="O conteúdo desta seção encontrou um erro. Troque de aba ou recarregue o app para tentar novamente."
          actionLabel="Recarregar app"
          variant="panel"
          resetKey={drawerSection}
        >
          <DrawerRouter />
        </AppErrorBoundary>
        <ModalPresenter locale="drawer" />
        <ToastPresenter />
      </OptionContainer>
      <MainDrawer data-testid="Drawer/MainDrawer" isOpen={isMainDrawerOpen} isFullscreen={isFullscreen}>
        <LogoContainer>
          <CompleteLogoNexAtlas width={132} />
        </LogoContainer>
        <MainDrawerContent $content_position="top">
          {topItems.map((item) => (
            <MainDrawerItem
              key={item.routePath}
              isActive={activeOption.startsWith(item.routePath)}
              onClick={() => handleOptionCLick(item.routePath)}
            >
              {item.title}
            </MainDrawerItem>
          ))}
        </MainDrawerContent>
        <MainDrawerContent $content_position="bottom">
          {bottomItems.map((item) => (
            <MainDrawerItem
              key={item.routePath}
              isActive={activeOption.startsWith(item.routePath)}
              onClick={() => handleOptionCLick(item.routePath)}
            >
              {item.title}
            </MainDrawerItem>
          ))}
        </MainDrawerContent>
      </MainDrawer>
    </Container>
  )
}

export { Drawer }
